/**
 * LinearConnectModal — paste-token (api-key) form for the Linear connector.
 *
 * Linear auth is an API KEY, not OAuth. Simpler than Plane: the user pastes
 * a SINGLE value:
 *   - API key  (personal API key from Linear → Settings → Security & access
 *               → Personal API keys; format `lin_api_...`)
 *
 * Backend validates (queries the authenticated viewer) before persisting —
 * structured error surfaces on err.message. Mirrors PlaneConnectModal /
 * CursorAdminConnectModal for the shared shape; just one field.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectLinear } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function LinearConnectModal({ isOpen, onClose, onConnected }) {
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectLinear();

  useEffect(() => {
    if (isOpen) {
      setApiKey('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!apiKey.trim()) {
      setError('API key is required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      { apiKey: apiKey.trim() },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect Linear. Please check your API key.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect Linear"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect Linear</h2>
        <p className="lark-modal-description">
          Create a personal API key in Linear → Settings → Security &amp; access →
          Personal API keys, then paste it below. Workflows can then read and
          update your Linear issues, comments, and workflow states.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="linear-api-key">API Key</label>
          <input
            id="linear-api-key"
            type="password"
            value={apiKey}
            placeholder="lin_api_..."
            onChange={(e) => { setApiKey(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !apiKey.trim()}
            aria-label="Submit Linear credentials"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel Linear connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
